import {
    Button,
    Flex,
    Image,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    useDisclosure,
    VStack,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    HStack,
    Badge,
} from "@chakra-ui/react";
import Template from "../../../../components/Layout/Template";
import { colors } from "../../../../styles";
import EditModal from "./Edit/EditModal";
import MyIcon from "../../../../components/Icon/MyIcon";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { IMyProfile, IUserType } from "../../../../types";
import {
    getMyProfile,
    getMyProfileChats,
    getMyProfileCommentedOnChats,
    getMyProfileSavedChats,
} from "../../../../api";
import { typeToKor } from "../../../../utils";
import { useEffect, useState } from "react";
import BlockingModal from "./BlockingModal";
import ProfileChatTabPanel from "../ProfileChatTabPanel";
import { useAuthStore } from "../../../../store";

export default function My() {

    /* query */

    const { data: myProfile } = useQuery<IMyProfile>({
        queryKey: ["getMyProfile"],
        queryFn: () => getMyProfile(),
        enabled: true,
    });

    const tabSelectedStyle = {
        color: colors.blue,
        fontWeight: 700,
        borderBottom: `2px solid ${colors.blue}`,
    };

    /* tab state */

    const [tabIndex, setTabIndex] = useState(0);
    const [visitedTabs, setVisitedTabs] = useState<Set<number>>(() => new Set([0]));

    const handleTabChange = (index: number) => {
        setTabIndex(index);
        setVisitedTabs((prev) => new Set(prev).add(index));
    };

    /* navigate */

    const navigate = useNavigate();

    /* 모달 상태 관리 */

    const { isOpen: isEditProfileOpen, onOpen: onEditProfileOpen, onClose: onEditProfileClose } = useDisclosure();
    const { isOpen: isBlockingModalOpen, onOpen: onBlockingModalOpen, onClose: onBlockingModalClose } = useDisclosure();

    /* render */

    return (
        <Template>
            {myProfile?.isMe ? (
                <VStack width={"100%"} alignItems={"center"}>
                    <Stack
                        width={{
                            base: "100%",
                            sm: "100%",
                            md: "100%",
                            lg: "80%",
                            xl: "80%",
                        }}
                        spacing={0}
                        paddingY={5}
                    >

                        {/* 프로필 영역 */}

                        <Flex
                            width={"100%"}
                            justifyContent={"space-between"}
                            alignItems={"flex-start"}
                        >
                            <VStack alignItems={"flex-start"} spacing={1}>
                                <HStack
                                    width={"100%"}
                                    justifyContent={"flex-start"}
                                    alignItems={"center"}
                                    spacing={2}
                                >
                                    <Text fontSize={24} fontWeight={700} color={colors.g1}>
                                        {myProfile?.nickname}
                                    </Text>
                                    <Badge
                                        backgroundColor={colors.blue}
                                        color={colors.white}
                                        borderRadius="full"
                                        paddingX={1}
                                        paddingY={0.5}
                                        fontSize={8}
                                        fontWeight={500}
                                    >
                                        나
                                    </Badge>
                                </HStack>
                                <Text fontSize={14} fontWeight={500} color={colors.g3}>
                                    {typeToKor(myProfile?.user.type as IUserType | null)}
                                </Text>
                            </VStack>

                            {myProfile?.avatar ? (
                                <Image
                                    src={myProfile?.avatar}
                                    alt={`${myProfile?.nickname} 프로필 이미지`}
                                    boxSize={{ base: 20, sm: 24 }}
                                    borderRadius={"full"}
                                    objectFit={"cover"}
                                    flexShrink={0}
                                />
                            ) : (
                                <Image 
                                    src={"/png/profile.png"}
                                    alt="프로필 이미지"
                                    boxSize={{ base: 20, sm: 24 }}
                                    borderRadius="full"
                                    objectFit="cover"
                                    flexShrink={0}
                                />
                            )}
                        </Flex>

                        {/* bio 영역 */}

                        {myProfile?.bio && (
                            <Text
                                marginTop={4}
                                fontSize={15}
                                fontWeight={400}
                                color={colors.g1}
                                whiteSpace={"pre-wrap"}
                            >
                                {myProfile?.bio}
                            </Text>
                        )}

                        {/* 프로필 편집 버튼 영역 */}

                        <Button
                            width={"100%"}
                            marginTop={5}
                            height={"40px"}
                            variant={"outline"}
                            borderColor={colors.g4}
                            borderRadius={"full"}
                            color={colors.black}
                            fontSize={14}
                            fontWeight={600}
                            _hover={{ backgroundColor: colors.g5 }}
                            onClick={onEditProfileOpen}
                        >
                            프로필 편집
                        </Button>

                        {/* 메뉴 영역 */}

                        <Tabs
                            isFitted
                            width={"100%"}
                            marginTop={6}
                            index={tabIndex}
                            onChange={handleTabChange}
                        >
                            <TabList borderBottom={`1px solid ${colors.g5}`}>
                                <Tab _selected={tabSelectedStyle} paddingY={3} fontSize={14} color={colors.g3}>
                                    글
                                </Tab>
                                <Tab _selected={tabSelectedStyle} paddingY={3} fontSize={14} color={colors.g3}>
                                    댓글단 글
                                </Tab>
                                <Tab _selected={tabSelectedStyle} paddingY={3} fontSize={14} color={colors.g3}>
                                    저장됨
                                </Tab>
                            </TabList>

                            <TabPanels>

                                <TabPanel paddingX={0} paddingTop={10}>
                                    <ProfileChatTabPanel
                                        queryKey={["getMyProfileChats"]}
                                        queryFn={(page, size) => getMyProfileChats({ page, size })}
                                        enabled={visitedTabs.has(0)}
                                        emptyMessage="게시글이 없습니다."
                                    />
                                </TabPanel>

                                <TabPanel paddingX={0} paddingTop={10}>
                                    <ProfileChatTabPanel
                                        queryKey={["getMyProfileCommentedOnChats"]}
                                        queryFn={(page, size) => getMyProfileCommentedOnChats({ page, size })}
                                        enabled={visitedTabs.has(1)}
                                        emptyMessage="댓글 단 게시글이 없습니다."
                                    />
                                </TabPanel>

                                <TabPanel paddingX={0} paddingTop={10}>
                                    <ProfileChatTabPanel
                                        queryKey={["getMyProfileSavedChats"]}
                                        queryFn={(page, size) => getMyProfileSavedChats({ page, size })}
                                        enabled={visitedTabs.has(2)}
                                        emptyMessage="저장된 게시글이 없습니다."
                                    />
                                </TabPanel>
                                
                            </TabPanels>
                        </Tabs>

                        <VStack
                            position={"fixed"}
                            right={{ base: 4, sm: 8, md: 12 }}
                            bottom={{ base: 4, sm: 8, md: 12 }}
                            spacing={{ base: 3, sm: 4, md: 4 }}
                        >
                            <Menu>
                                <MenuButton
                                    as={IconButton}
                                    aria-label='Options'
                                    icon={<MyIcon src={"/svg/more-blue.svg"} alt={"더보기"} width={20} height={20} />}
                                    variant='ghost'
                                    boxSize={{ base: 12, sm: 16, md: 20 }}
                                    minWidth={{ base: 12, sm: 16, md: 20 }}
                                    borderRadius={"full"}
                                    backgroundColor={colors.light}
                                    boxShadow={"lg"}
                                />
                                    <MenuList>
                                        <MenuItem onClick={onBlockingModalOpen}>
                                            차단한 사용자 보기
                                        </MenuItem>
                                        <MenuItem 
                                            as={Link}
                                            to={`/chat/profile/my/likes`}
                                        >
                                            내가 좋아요 누른 글 보기
                                        </MenuItem>
                                    </MenuList>
                            </Menu>
                        </VStack>
                    </Stack>
                </VStack>
            ) : (
                <VStack 
                    width={"100%"}
                    height={"80vh"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    spacing={4}
                >
                    <Text fontSize={14} fontWeight={500} color={colors.g3} textAlign={"center"}>
                        접근 권한이 없습니다.
                    </Text>
                </VStack>
            )}

            <EditModal isOpen={isEditProfileOpen} onClose={onEditProfileClose} />
            <BlockingModal isOpen={isBlockingModalOpen} onClose={onBlockingModalClose} />
                    
        </Template>
    );
}
